
import React, { useRef } from 'react'

/**
 * HOC: withNodeOptimization
 *
 * Optimizes any node component to prevent unnecessary re-renders.
 *
 * Features:
 * - Custom React.memo comparator (only re-render on meaningful prop changes)
 * - Stable callback references with useCallback
 * - Render logging for debugging (only in local environment)
 *
 * Usage:
 * ```tsx
 * const OptimizedTextFile = withNodeOptimization(TextFile)
 * ```
 */

interface NodeOptimizationConfig {
  // Props to compare for re-render decision
  compareProps?: string[]
  // Enable render logging (default: only in local environment)
  enableLogging?: boolean
}

const defaultConfig: NodeOptimizationConfig = {
  compareProps: ['id', 'selected', 'width', 'height'],
  // Enable logging only in local environment for debugging
  // Set to true manually when debugging: withNodeOptimization(Component, { enableLogging: true })
  enableLogging: false
}

export function withNodeOptimization<P extends object>(
  Component: React.ComponentType<P>,
  config: NodeOptimizationConfig = {}
) {
  const finalConfig = { ...defaultConfig, ...config }

  function OptimizedComponent(props: P) {
    // 🔍 RENDER LOGGER
    const renderCount = useRef(0)
    const prevPropsRef = useRef(props)

    if (finalConfig.enableLogging) {
      renderCount.current++
      prevPropsRef.current = props
    }

    // Don't stabilize callbacks - pass them directly
    // The parent component is responsible for memoizing callbacks
    return <Component {...props} />
  }

  OptimizedComponent.displayName = `withNodeOptimization(${Component.displayName || Component.name})`

  // Custom comparator — shallow-compares object props (like `state`) instead of
  // reference equality, so spreading { ...genieState } doesn't defeat the memo
  function arePropsEqual(prevProps: P, nextProps: P): boolean {
    const prevRecord = prevProps as Record<string, unknown>
    const nextRecord = nextProps as Record<string, unknown>
    const compareList = finalConfig.compareProps || Object.keys(prevRecord)

    for (const key of compareList) {
      const a = prevRecord[key]
      const b = nextRecord[key]
      if (a === b) continue
      // For plain objects, do one-level shallow comparison
      if (typeof a === 'object' && typeof b === 'object' && a !== null && b !== null && !Array.isArray(a)) {
        const keysA = Object.keys(a as Record<string, unknown>)
        const keysB = Object.keys(b as Record<string, unknown>)
        if (keysA.length !== keysB.length) return false
        const objA = a as Record<string, unknown>
        const objB = b as Record<string, unknown>
        for (const k of keysA) {
          if (objA[k] !== objB[k]) return false
        }
        continue
      }
      return false
    }

    return true
  }

  return React.memo(OptimizedComponent, arePropsEqual)
}
